// frontend/src/pages/ForgotPassword.js

import React, { memo, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../api";
import PreLoginHeader from "../components/PreLoginHeader";

const OceanSparkleBackground = memo(function OceanSparkleBackground() {
  return (
    <div aria-hidden="true" className="ocean-background">
      <div className="ocean-light" />

      <div className="ocean-wave ocean-wave-one" />
      <div className="ocean-wave ocean-wave-two" />
      <div className="ocean-wave ocean-wave-three" />

      <div className="ocean-clouds">
        <div className="ocean-cloud ocean-cloud-one" />
        <div className="ocean-cloud ocean-cloud-two" />
        <div className="ocean-cloud ocean-cloud-three" />
        <div className="ocean-cloud ocean-cloud-four" />
        <div className="ocean-cloud ocean-cloud-five" />
      </div>

      <div className="ocean-wash" />
    </div>
  );
});

const DesktopBackgroundText = memo(function DesktopBackgroundText() {
  return (
    <div
      aria-hidden="true"
      className="
        pointer-events-none absolute inset-0 z-[2]
        hidden select-none overflow-hidden lg:block
      "
    >
      <p
        className="
          absolute -left-2 top-10 max-w-[390px]
          text-[78px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-50
          xl:text-[92px]
        "
      >
        where
        <br />
        every day
        <br />
        is a win
      </p>

      <p
        className="
          absolute right-[9%] top-[54%] max-w-[500px]
          text-[68px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-100
          xl:text-[64px]
        "
      >
        find your
        <br />
        community
      </p>

      <p
        className="
          absolute right-0 top-[30%] max-w-[590px]
          text-right text-[68px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-100
          xl:text-[64px]
        "
      >
        do more of
        <br />
        what you love
      </p>

      <p
        className="
          absolute bottom-[8%] right-0 max-w-[530px]
          text-right text-[66px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-100
          xl:text-[64px]
        "
      >
        celebrate your
        <br />
        every step
      </p>
    </div>
  );
});

export default function ForgotPassword() {
  const emailRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [signupUrl, setSignupUrl] = useState("");
  const [error, setError] = useState("");
  const [canSubmit, setCanSubmit] = useState(false);

  const navigate = useNavigate();

  const checkCanSubmit = useCallback(() => {
    const hasEmail = Boolean(emailRef.current?.value.trim());

    setCanSubmit((previousValue) =>
      previousValue === hasEmail ? previousValue : hasEmail
    );
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");
    setMsg("");
    setSignupUrl("");
    setLoading(true);

    const email = emailRef.current?.value.trim() || "";

    try {
      const { data } = await api.post("/auth/password/forgot", {
        email,
      });

      setMsg(
        data?.message ||
          "If that email exists in our system, we’ve sent a password reset link."
      );

      setSignupUrl(data?.signup_url || "");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Network or server error";

      setError(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 text-[#181411]">
      <section className="relative min-h-screen overflow-hidden">
        <OceanSparkleBackground />
        <PreLoginHeader />
        <DesktopBackgroundText />

        <div
          className="
            relative z-10 flex min-h-screen
            items-center justify-center
            px-6 pb-14 sm:pb-10 pt-20 sm:pt-24
          "
        >
          <main className="flex w-full max-w-[400px] flex-col items-center">
            <h1
              className="
                logo-font mb-8 select-none
                text-[60px] font-black leading-[0.9]
                tracking-[-0.06em] text-blue-500
                drop-shadow-[0_2px_14px_rgba(255,255,255,0.85)]
                sm:text-[72px]
              "
            >
              third place
            </h1>

            <div
              className="
                w-full max-w-[390px] -mt-1
                rounded-[32px] bg-slate-50/80
                px-4 py-5
                shadow-[0_24px_70px_rgba(22,59,86,0.04)]
                sm:p-5
              "
            >
              <form className="space-y-2" onSubmit={handleSubmit}>
                <div className="px-3 pb-1 text-start">
                  <h2 className="text-lg font-black tracking-[-0.02em] text-[#181411]">
                    Reset your password
                  </h2>

                  <p className="text-sm leading-tight text-[#8a8078]">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>

                {error && (
                  <p
                    className="
                      -mt-1 rounded-2xl
                      px-4 pb-1 text-left
                      text-sm text-red-600
                    "
                    aria-live="polite"
                  >
                    {error}
                  </p>
                )}

                {msg && (
                  <div
                    className="
                      rounded-2xl bg-blue-50/80
                      px-4 py-3 text-left
                      text-sm leading-relaxed text-[#6f665d]
                    "
                    aria-live="polite"
                  >
                    <span>{msg}</span>

                    {signupUrl && (
                      <>
                        {" "}
                        <button
                          type="button"
                          onClick={() => navigate(signupUrl)}
                          className="
                            !min-h-0 !min-w-0
                            font-semibold text-blue-400
                            transition hover:font-bold
                          "
                        >
                          Sign up here
                        </button>
                        <span>.</span>
                      </>
                    )}
                  </div>
                )}

                <div>
                  <label
                    htmlFor="forgot-password-email"
                    className="
                      mb-1.5 ml-3 block
                      text-sm font-semibold text-[#6f665d]
                    "
                  >
                    Email
                  </label>

                  <input
                    id="forgot-password-email"
                    ref={emailRef}
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="you@example.com"
                    onInput={checkCanSubmit}
                    onChange={checkCanSubmit}
                    onFocus={checkCanSubmit}
                    className="
                      w-full rounded-3xl
                      border border-white/80 bg-blue-50/85
                      !min-h-0 !min-w-0 !px-3 !py-1.5 text-sm outline-none
                      placeholder:text-slate-400
                      focus:border-blue-500 focus:border-2
                    "
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !canSubmit}
                  className="
                    w-full transform-gpu rounded-full
                    bg-blue-600 !min-h-0 !min-w-0 !py-3 !mt-4
                    text-sm font-semibold text-white
                    transition-all duration-300 ease-out
                    hover:scale-[1.03]
                    hover:bg-blue-700
                    active:scale-[0.99]
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                    disabled:hover:scale-100
                    disabled:hover:bg-blue-600
                  "
                >
                  {loading ? "sending…" : "Send reset link"}
                </button>

                <div className="text-center text-xs text-[#8a8078] !mt-4">
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="
                      !min-h-0 !min-w-0
                      font-semibold text-blue-400
                      transition hover:font-bold
                    "
                  >
                    Back to sign in
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/signup")}
                    className="
                      ml-3 !min-h-0 !min-w-0
                      font-medium text-blue-400
                      transition hover:font-bold
                    "
                  >
                    Sign up
                  </button>
                </div>
              </form>
            </div>
          </main>
        </div>
      </section>
    </div>
  );
}
